import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import sortBy from "lodash/sortBy";

import { Tooltip } from "fe-shared/src/components";
import { TireMaterialConfig } from "fe-shared/src/types/domain";

import { RowInvoice, Retreader, TransactionMaterial } from "../../types";
import { CollectionInfoTooltip, InvoiceRows } from "../index";

import styles from "./DetailsDataSection.module.scss";

interface Props {
  transaction: Retreader;
  materials: TireMaterialConfig[];
}

const ReTreaderDataInfo = ({ transaction, materials }: Props) => {
  const { t } = useTranslation();
  const {
    isKnownRetreaderTiresActualWeight,
    collectionInfoMaterials,
    retreadRepairInfoMaterials,
    recoveryInfoMaterials,
    recoveryGain,
    comment,
  } = transaction;

  const sortingOrder = useMemo(
    () => materials.map((material) => material.code),
    [materials]
  );
  const sortedRetreadRepairInfoMaterials = useMemo(() => {
    return sortBy(retreadRepairInfoMaterials, (m: TransactionMaterial) =>
      sortingOrder.indexOf(m.materialCode)
    ) as TransactionMaterial[];
  }, [sortingOrder, retreadRepairInfoMaterials]);
  const sortedCollectionInfoMaterials = useMemo(() => {
    return sortBy(collectionInfoMaterials, (m: TransactionMaterial) =>
      sortingOrder.indexOf(m.materialCode)
    ) as TransactionMaterial[];
  }, [sortingOrder, collectionInfoMaterials]);

  const collectionRows: RowInvoice[] = useMemo(() => {
    return [
      {
        key: "collectionTitle",
        name: (
          <div className={styles.retreadingCollectionInfoLabel}>
            {t("transactions.details.collectionInfo")}
            <CollectionInfoTooltip materials={materials} />
          </div>
        ),
        value: "",
        isGrandTotal: true,
      },
      {
        key: "collectionHeader",
        name: t("categories.Tires", { unit: "units" }) as string,
        value: t(
          isKnownRetreaderTiresActualWeight
            ? "transactions.details.estimatedUnits"
            : "units.Units"
        ) as string,
        additionalValue: t(
          `transactions.details.${
            isKnownRetreaderTiresActualWeight ? "realWeight" : "estimatedWeight"
          }`
        ) as string,
        isTitle: true,
        valuesStyles: styles.retreadingTableHeader,
      },
      ...sortedCollectionInfoMaterials.map((material, index) => ({
        key: `collection-${material.materialCode}`,
        name: material.materialName,
        value: Math.ceil(material.units.unitQuantity),
        additionalValue: material.units.weight,
        withBorder: index === collectionInfoMaterials.length - 1,
      })),
    ];
  }, [
    collectionInfoMaterials.length,
    isKnownRetreaderTiresActualWeight,
    materials,
    sortedCollectionInfoMaterials,
    t,
  ]);

  const retreadingRows: RowInvoice[] = useMemo(() => {
    return [
      {
        key: "retreadRegainTitle",
        name: t("transactions.details.retreadRegainInfo") as string,
        value: "",
        isGrandTotal: true,
      },
      {
        key: "retreadRegainHeader",
        name: t("categories.Tires", { unit: "units" }) as string,
        value: t("units.Units") as string,
        additionalValue: t("transactions.details.realWeight") as string,
        isTitle: true,
        valuesStyles: styles.retreadingTableHeader,
      },
      ...sortedRetreadRepairInfoMaterials.map((material) => ({
        key: `collection-${material.materialCode}`,
        name: material.materialName,
        value: Math.ceil(material.units.unitQuantity),
        additionalValue: material.units.weight,
      })),
    ];
  }, [sortedRetreadRepairInfoMaterials, t]);

  const recoveryRows: RowInvoice[] = useMemo(() => {
    return [
      {
        key: "recoveryInfoHeader",
        name: t("transactions.details.recoveryInfo") as string,
        value: t("transactions.details.realWeight") as string,
        isTitle: true,
        valuesStyles: styles.retreadingTableHeader,
      },
      ...recoveryInfoMaterials.map((material) => ({
        key: `collection-${material.materialCode}`,
        name: material.materialName,
        value: material.units.unitQuantity,
      })),
      {
        key: "recoveryGain",
        name: (
          <div className={styles.retreadingRecoveryGainLabel}>
            {t("transactions.details.recoveryGain")}
            <Tooltip
              wrapperClassName={styles.retreadingRecoveryGainTooltipWrapper}
            >
              {t("transactions.details.recoveryGainTooltip")}
            </Tooltip>
          </div>
        ),
        value: recoveryGain,
      },
    ];
  }, [recoveryGain, recoveryInfoMaterials, t]);

  return (
    <div className={styles.retreadingWrapper}>
      <table
        className={classnames(
          styles.retreadingDetails,
          styles.retreadingCollectionInfo
        )}
      >
        <tbody>
          {collectionRows.map((row) => {
            return <InvoiceRows key={row.key} {...row} />;
          })}
        </tbody>
      </table>
      <table className={styles.retreadingDetails}>
        <tbody>
          {retreadingRows.map((row) => {
            return <InvoiceRows key={row.key} {...row} />;
          })}
        </tbody>
      </table>
      <table className={styles.retreadingDetails}>
        <tbody>
          {recoveryRows.map((row) => {
            return <InvoiceRows key={row.key} {...row} />;
          })}
        </tbody>
      </table>

      {comment && (
        <>
          <div className={styles.commentWrapper}>
            <h4>{t("transactions.details.comments")}</h4>
            <div className={styles.comment}>{comment}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReTreaderDataInfo;
